import { lazy, ReactNode, Suspense } from "react";
import { useUser } from "@/hooks/use-user.ts";
import { useOptionalSelectedWorkspace } from "@/hooks/use-selected-workspace.ts";

// Rarely up, and it renders the terms with the Markdown renderer
const AcceptTermsModal = lazy(
    () => import("@/features/Terms/AcceptTermsModal.tsx"),
);

/**
 * Whether the blocking terms-acceptance modal is up. Only org admins/owners
 * are prompted, since only they can accept on the organization's behalf.
 * Other dialogs wait for it rather than open on top of it.
 */
export function useTermsAcceptancePending(): boolean {
    const user = useUser();
    const selectedWorkspace = useOptionalSelectedWorkspace();

    return (
        !!selectedWorkspace &&
        user.getCurrentOrganization().needsTermsAcceptance() &&
        user.hasAdminPermissions() &&
        !user?.managesOrganization(selectedWorkspace.organization) &&
        !user.isSuperAdmin()
    );
}

/**
 * Shows a blocking terms-acceptance modal over the app when the current
 * organization has not accepted the latest terms version.
 */
export default function TermsAcceptanceGuard({
    children,
}: {
    children: ReactNode;
}) {
    const user = useUser();
    const organization = user.getCurrentOrganization();
    const shouldPrompt = useTermsAcceptancePending();

    return (
        <>
            {children}
            {shouldPrompt && (
                <Suspense fallback={null}>
                    <AcceptTermsModal
                        version={organization.currentTermsVersion}
                    />
                </Suspense>
            )}
        </>
    );
}
